/**
 * Platform open-world areas, grouped by planet, for area pickers.
 *
 * Fetched once per page load and shared by every caller (the list changes only
 * when the platform publishes a release). A failed load is not cached, so the
 * next mount retries.
 */

import { useEffect, useMemo, useState } from 'react';
import type { PlatformAreaOption, PlatformPlanetGroup } from '@swtorbit/shared-socket-events';
import { fetchPlatformAreas } from '../utils/platformApi';

let shared: Promise<PlatformPlanetGroup[]> | null = null;

function loadPlanets(): Promise<PlatformPlanetGroup[]> {
  shared ??= fetchPlatformAreas()
    .then((r) => r.planets)
    .catch((error: unknown) => {
      shared = null;
      throw error;
    });
  return shared;
}

/** One area plus the planet it sits under. */
export type PlatformAreaEntry = PlatformAreaOption & { planetName: string };

export interface UsePlatformAreas {
  planets: PlatformPlanetGroup[];
  /** Every area, keyed by its combat-log area id. */
  byId: Map<number, PlatformAreaEntry>;
  loading: boolean;
  error: string | null;
}

export function usePlatformAreas(): UsePlatformAreas {
  const [planets, setPlanets] = useState<PlatformPlanetGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    loadPlanets()
      .then((p) => { if (live) { setPlanets(p); setError(null); } })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load areas'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const byId = useMemo(() => {
    const map = new Map<number, PlatformAreaEntry>();
    for (const planet of planets) {
      for (const area of planet.areas) map.set(area.logAreaId, { ...area, planetName: planet.planetName });
    }
    return map;
  }, [planets]);

  return { planets, byId, loading, error };
}
